import { Fragment, type FormEvent, useMemo, useState } from "react";

import { useApiUrl } from "@refinedev/core";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  ListView,
  ListViewHeader,
} from "@/components/refine-ui/views/list-view";
import { DatePickerWithRange } from "@/components/fields/date-picker";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { cn } from "@/lib/utils";

import { previewBlend } from "./api";
import type {
  BlendCandidateSource,
  BlendEmptyReason,
  BlendEvent,
  BlendGraphStatus,
  BlendMember,
  BlendMemberScore,
  BlendPreviewPayload,
  BlendPromptIntent,
} from "./types";

type DatePreset = "tonight" | "weekend" | "next2weeks" | "custom";

type CustomRange = { from?: Date; to?: Date };

export function BlendLabPage() {
  const apiUrl = useApiUrl();

  const [emailsRaw, setEmailsRaw] = useState("");
  const [cityName, setCityName] = useState("Athens");
  const [country, setCountry] = useState("Greece");
  const [datePreset, setDatePreset] = useState<DatePreset>("next2weeks");
  const [customRange, setCustomRange] = useState<CustomRange>({});
  const [lambda, setLambda] = useState(0.5);
  const [crewDescriptor, setCrewDescriptor] = useState("");
  const [topN, setTopN] = useState("20");
  const [polish, setPolish] = useState(false);
  const [anchorLow, setAnchorLow] = useState("");
  const [anchorHigh, setAnchorHigh] = useState("");
  const [includeEventIdsRaw, setIncludeEventIdsRaw] = useState("");
  const [expandedEventIds, setExpandedEventIds] = useState<Set<string>>(
    new Set(),
  );

  const mutation = useMutation({
    mutationFn: (payload: BlendPreviewPayload) => previewBlend(apiUrl, payload),
    onSuccess: () => setExpandedEventIds(new Set()),
    onError: (error) => toast.error(extractErrorMessage(error)),
  });

  const response = mutation.data;

  const memberNameById = useMemo(
    () => new Map(response?.members.map((m) => [m.userId, m.displayName]) ?? []),
    [response],
  );

  function toggleExpanded(eventId: string) {
    setExpandedEventIds((current) => {
      const next = new Set(current);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const emails = parseEmails(emailsRaw);
    if (emails.length === 0) {
      toast.error("Enter at least one member email.");
      return;
    }

    const range = buildDateRange(datePreset, customRange);
    if (!range) {
      toast.error("Pick a custom date range first.");
      return;
    }

    const includeEventIds = parseCommaList(includeEventIdsRaw);
    const parsedTopN = Number(topN);

    // Anchors are optional overrides: blank means "use the server's calibrated default", so an
    // unparseable or empty box must send nothing rather than a NaN the endpoint would 400 on.
    const parsedAnchorLow = parseOptionalNumber(anchorLow);
    const parsedAnchorHigh = parseOptionalNumber(anchorHigh);
    if (
      parsedAnchorLow !== undefined &&
      parsedAnchorHigh !== undefined &&
      parsedAnchorHigh <= parsedAnchorLow
    ) {
      toast.error("AnchorHigh must be greater than AnchorLow.");
      return;
    }

    const payload: BlendPreviewPayload = {
      Emails: emails,
      CityName: cityName.trim() || "Athens",
      Country: country.trim() || "Greece",
      FromUnixSeconds: range.from,
      ToUnixSeconds: range.to,
      IncludeEventIds: includeEventIds.length > 0 ? includeEventIds : undefined,
      CrewDescriptor: crewDescriptor.trim() || undefined,
      Polish: polish,
      Lambda: lambda,
      TopN: Number.isFinite(parsedTopN) && parsedTopN > 0 ? parsedTopN : 20,
      AnchorLow: parsedAnchorLow,
      AnchorHigh: parsedAnchorHigh,
    };

    mutation.mutate(payload);
  }

  return (
    <ListView className="p-6 gap-4">
      <ListViewHeader title="Blend Lab" />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Blend inputs</CardTitle>
          <CardDescription>
            Preview how BlendEngine would rank events for a group of members.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
          >
            <Field label="Member emails" className="md:col-span-2 xl:col-span-4">
              <Textarea
                value={emailsRaw}
                onChange={(event) => setEmailsRaw(event.target.value)}
                placeholder={"one@mood.com, two@mood.com\nor one per line"}
                rows={3}
              />
            </Field>
            <Field label="City">
              <Input
                value={cityName}
                onChange={(event) => setCityName(event.target.value)}
                placeholder="Athens"
              />
            </Field>
            <Field label="Country">
              <Input
                value={country}
                onChange={(event) => setCountry(event.target.value)}
                placeholder="Greece"
              />
            </Field>
            <Field label="Date range" className="md:col-span-2">
              <div className="flex flex-col gap-2">
                <Select
                  value={datePreset}
                  onValueChange={(value) => setDatePreset(value as DatePreset)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tonight">Tonight</SelectItem>
                    <SelectItem value="weekend">This weekend</SelectItem>
                    <SelectItem value="next2weeks">Next 2 weeks</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {datePreset === "custom" && (
                  <DatePickerWithRange
                    className="[&_button]:w-full"
                    defaultFrom={customRange.from}
                    defaultTo={customRange.to}
                    onDateRangeChanged={(range) =>
                      setCustomRange({ from: range?.from, to: range?.to })
                    }
                  />
                )}
              </div>
            </Field>
            <Field
              label={`λ (lambda) — ${lambda.toFixed(2)}`}
              className="md:col-span-2"
            >
              <Slider
                value={[lambda]}
                min={0}
                max={1}
                step={0.05}
                onValueChange={(values) => setLambda(values[0] ?? 0.5)}
              />
              <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>0 — pure mean</span>
                <span>1 — least misery</span>
              </div>
            </Field>
            <Field label="Crew descriptor" className="md:col-span-2">
              <Input
                value={crewDescriptor}
                onChange={(event) => setCrewDescriptor(event.target.value)}
                placeholder="Filters or boosts — e.g. 'only techno' or 'chill date night'"
              />
            </Field>
            <Field label="Top N">
              <Input
                type="number"
                min={1}
                max={100}
                value={topN}
                onChange={(event) => setTopN(event.target.value)}
              />
            </Field>
            <Field label="Polish (Gemini group sentence)">
              <div className="flex h-10 items-center">
                <Switch checked={polish} onCheckedChange={setPolish} />
              </div>
            </Field>
            <Field label="Anchor low (blank = default)">
              <Input
                type="number"
                step="0.001"
                min={0}
                max={1}
                value={anchorLow}
                onChange={(event) => setAnchorLow(event.target.value)}
                placeholder="0.92"
              />
            </Field>
            <Field label="Anchor high (blank = default)">
              <Input
                type="number"
                step="0.001"
                min={0}
                max={1}
                value={anchorHigh}
                onChange={(event) => setAnchorHigh(event.target.value)}
                placeholder="0.975"
              />
            </Field>
            <Field
              label="Include event IDs"
              className="md:col-span-2 xl:col-span-4"
            >
              <Input
                value={includeEventIdsRaw}
                onChange={(event) => setIncludeEventIdsRaw(event.target.value)}
                placeholder="Comma-separated — e.g. force an off-city event into the pool"
              />
            </Field>
            <div className="flex justify-end md:col-span-2 xl:col-span-4">
              <Button
                type="submit"
                className="gap-2"
                disabled={mutation.isPending || emailsRaw.trim().length === 0}
              >
                {mutation.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Preview blend
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Results</CardTitle>
          <CardDescription>
            Ranked events for the loaded members, mirroring BlendEngine&apos;s
            output.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mutation.isPending ? (
            <ResultsSkeleton />
          ) : !response ? (
            <EmptyState
              title="No preview yet"
              description="Fill in the controls above and submit to preview a blend."
            />
          ) : (
            <>
              <ResultBanners
                staleMix={response.staleMix}
                emptyReason={response.emptyReason}
                droppedCount={response.droppedEventIds.length}
                candidateSource={response.candidateSource}
                graphStatus={response.graphStatus}
              />
              <PromptIntentPanel
                intent={response.promptIntent ?? null}
                relaxed={response.promptRelaxed ?? false}
              />
              <MemberStrip members={response.members} />
              {response.events.length === 0 ? (
                <EmptyState
                  title="No events"
                  description="The engine returned zero candidates for this window."
                />
              ) : (
                <EventsTable
                  events={response.events}
                  memberNameById={memberNameById}
                  expandedEventIds={expandedEventIds}
                  onToggleExpanded={toggleExpanded}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </ListView>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-xs font-medium text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

// Unresolved emails never reach here: the endpoint 400s on them before running the
// engine, and the error surfaces through the mutation's toast.
//
// The no-events states are reported SEPARATELY and worded for what actually happened. They used
// to collapse into emptyPool, so an all-LowSignal crew and a Typesense outage both read as "No
// events match the filters" — a claim about event supply, in the one tool built to diagnose the
// engine, for a window that may well be full of events. Only EmptyPool is destructive-styled: it
// is the only one that is a statement about the data.
//
// graphStatus and candidateSource get the same treatment, and for the same reason this backend
// change happened at all: `NoReach` (the graph ran and found nothing for this crew) and
// `Unavailable` (the graph could not run — BigQuery outage, a raised exception, an empty events
// snapshot) must never look alike. NoReach is a fact about the crew, so it renders as a plain,
// non-destructive alert; Unavailable is a fact about our infrastructure and is destructive-styled
// so it stands out from every other banner here. `EmbeddingFallback` gets its own banner too,
// worded differently depending on whether the graph was Ok (it found events but Typesense's
// liveness re-query dropped all of them) or not (the graph pool was simply empty).
function ResultBanners({
  staleMix,
  emptyReason,
  droppedCount,
  candidateSource,
  graphStatus,
}: {
  staleMix: boolean;
  emptyReason: BlendEmptyReason;
  droppedCount: number;
  candidateSource: BlendCandidateSource;
  graphStatus: BlendGraphStatus;
}) {
  const hasEngineNotice =
    graphStatus !== "Ok" || candidateSource === "EmbeddingFallback";

  if (
    !staleMix &&
    emptyReason === "None" &&
    droppedCount === 0 &&
    !hasEngineNotice
  ) {
    return null;
  }

  return (
    <div className="space-y-2">
      {graphStatus === "Unavailable" && (
        <Alert variant="destructive">
          <AlertTitle>Graph unavailable</AlertTitle>
          <AlertDescription>
            The graph could not run — a BigQuery outage, a raised exception in the
            routine, or an empty events snapshot. This is an infrastructure fault,
            not a fact about the crew. Check the Admin.API logs.
          </AlertDescription>
        </Alert>
      )}
      {graphStatus === "NoReach" && (
        <Alert>
          <AlertTitle>Graph: no reach</AlertTitle>
          <AlertDescription>
            The graph ran and found nothing for this crew in this window — a fact
            about the crew (unrelated members, too narrow a window), not an outage.
          </AlertDescription>
        </Alert>
      )}
      {candidateSource === "EmbeddingFallback" && (
        <Alert>
          <AlertTitle>Embedding fallback candidates</AlertTitle>
          <AlertDescription>
            {graphStatus === "Ok"
              ? "The graph walk found events, but the Typesense liveness re-query returned none of them, so a single crew-averaged similarity search filled the pool instead."
              : "The graph pool was empty, so a single crew-averaged similarity search supplied candidates instead."}
          </AlertDescription>
        </Alert>
      )}
      {staleMix && (
        <Alert>
          <AlertTitle>Stale mix</AlertTitle>
          <AlertDescription>
            Nothing reached the Stretch bar, so nothing cleared the return floor.
          </AlertDescription>
        </Alert>
      )}
      {emptyReason === "EmptyPool" && (
        <Alert variant="destructive">
          <AlertTitle>Empty pool</AlertTitle>
          <AlertDescription>
            No events match the filters — every candidate source answered, and this
            city/window/status combination has nothing in it.
          </AlertDescription>
        </Alert>
      )}
      {emptyReason === "NoScoringMembers" && (
        <Alert>
          <AlertTitle>No scoring members</AlertTitle>
          <AlertDescription>
            Every member is LowSignal (no embedding and no genre vectors), so there
            was no query vector to retrieve with and nobody to score for. This says
            nothing about whether the window has events — try members with a
            Full or SyntheticGenres signal.
          </AlertDescription>
        </Alert>
      )}
      {emptyReason === "CandidateSourceUnavailable" && (
        <Alert>
          <AlertTitle>Candidate source unavailable</AlertTitle>
          <AlertDescription>
            A retrieval source failed to answer, so the candidate pool is unknown
            rather than empty. Check the Admin.API logs for the Typesense error —
            this is not a statement about event supply.
          </AlertDescription>
        </Alert>
      )}
      {emptyReason === "AllCandidatesUnscorable" && (
        <Alert>
          <AlertTitle>All candidates unscorable</AlertTitle>
          <AlertDescription>
            Candidates came back, but not one of them could be scored for any
            member — e.g. every candidate is missing a readable embedding. This is
            not a statement about whether the window has events.
          </AlertDescription>
        </Alert>
      )}
      {droppedCount > 0 && (
        <Alert>
          <AlertTitle>Dropped candidates</AlertTitle>
          <AlertDescription>
            {droppedCount} candidate event{droppedCount === 1 ? "" : "s"} dropped
            — missing a readable embedding.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// What the interpreter DECIDED, not what the operator typed: the mode (filter vs boost), the
// terms it extracted per dimension, and any date range it resolved. Rendered whenever an intent
// came back so an operator never has to infer the model's reading from the ranking — and the
// relaxed case is called out explicitly, because a full board under a relaxed filter would
// otherwise read as "all of these match the prompt".
function PromptIntentPanel({
  intent,
  relaxed,
}: {
  intent: BlendPromptIntent | null;
  relaxed: boolean;
}) {
  if (!intent) return null;

  const dimensions: Array<{ label: string; terms: string[] }> = [
    { label: "Genres", terms: intent.genres },
    { label: "Event types", terms: intent.eventTypes },
    { label: "Artists", terms: intent.artists },
    { label: "Venues", terms: intent.venues },
  ].filter((dimension) => dimension.terms.length > 0);

  const dateRange =
    intent.fromUnixSeconds !== null || intent.toUnixSeconds !== null
      ? `${formatIntentDate(intent.fromUnixSeconds)} → ${formatIntentDate(intent.toUnixSeconds)}`
      : null;

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Prompt intent
        </span>
        <Badge
          variant="outline"
          className={promptModeBadgeClassName(intent.mode, relaxed)}
        >
          {relaxed ? "Filter → relaxed to Boost" : intent.mode}
        </Badge>
        {dimensions.map((dimension) => (
          <Fragment key={dimension.label}>
            <span className="text-[10px] uppercase text-muted-foreground">
              {dimension.label}
            </span>
            {dimension.terms.map((term) => (
              <Badge key={term} variant="secondary" className="text-[10px]">
                {term}
              </Badge>
            ))}
          </Fragment>
        ))}
        {dateRange && (
          <>
            <span className="text-[10px] uppercase text-muted-foreground">
              Dates
            </span>
            <Badge variant="secondary" className="text-[10px]">
              {dateRange}
            </Badge>
          </>
        )}
        {dimensions.length === 0 && !dateRange && (
          <span className="text-xs text-muted-foreground">
            No structural terms — semantic (embedding) match only.
          </span>
        )}
      </div>
      {relaxed && (
        <Alert>
          <AlertTitle>Prompt filter relaxed</AlertTitle>
          <AlertDescription>
            Too few candidates matched the prompt&apos;s constraints, so the
            engine kept the whole pool and boosted matches instead. The board is
            full, but the constraint was NOT applied — do not read these events
            as all matching the prompt.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function promptModeBadgeClassName(
  mode: BlendPromptIntent["mode"],
  relaxed: boolean,
): string {
  if (relaxed)
    return "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300";
  switch (mode) {
    case "Filter":
      return "border-blue-500/20 bg-blue-500/10 text-blue-700 dark:text-blue-300";
    case "Boost":
      return "border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300";
    default:
      return "";
  }
}

/** Open-ended sides render as "…": the intent may constrain only one end of the range. */
function formatIntentDate(unixSeconds: number | null): string {
  return unixSeconds === null
    ? "…"
    : dayjs(unixSeconds * 1000).format("ddd D MMM");
}

function MemberStrip({ members }: { members: BlendMember[] }) {
  if (members.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {members.map((member) => (
        <Tooltip key={member.userId}>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={cn("cursor-default", signalBadgeClassName(member.signal))}
            >
              {member.displayName}
              <span className="ml-1.5 text-[10px] uppercase opacity-70">
                {member.signal}
              </span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {member.topGenres.length > 0
              ? member.topGenres.join(", ")
              : "No genres on file"}
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

function EventsTable({
  events,
  memberNameById,
  expandedEventIds,
  onToggleExpanded,
}: {
  events: BlendEvent[];
  memberNameById: Map<string, string>;
  expandedEventIds: Set<string>;
  onToggleExpanded: (eventId: string) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead className="w-20 text-right">Fit %</TableHead>
          <TableHead>Name</TableHead>
          <TableHead className="w-44">Date</TableHead>
          <TableHead>Venue</TableHead>
          <TableHead className="w-56">Note</TableHead>
          <TableHead>Group explanation</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {events.map((event) => {
          const isExpanded = expandedEventIds.has(event.eventId);
          return (
            <Fragment key={event.eventId}>
              <TableRow
                className="cursor-pointer"
                onClick={() => onToggleExpanded(event.eventId)}
              >
                <TableCell>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="font-bold">{event.fitPercent}%</div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">
                    {event.name ?? event.eventId}
                  </div>
                  {event.genreNames.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {event.genreNames.slice(0, 3).map((genre) => (
                        <Badge
                          key={genre}
                          variant="secondary"
                          className="text-[10px]"
                        >
                          {genre}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {event.startDate
                    ? new Date(event.startDate * 1000).toLocaleString()
                    : "—"}
                </TableCell>
                <TableCell className="text-sm">
                  {event.venueName ?? "—"}
                  {event.cityName && (
                    <div className="text-xs text-muted-foreground">
                      {event.cityName}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {event.note !== "None" ? (
                      <Badge
                        variant="outline"
                        className={noteBadgeClassName(event.note)}
                      >
                        {noteLabel(event, memberNameById)}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                    {event.isNew && <Badge variant="outline">New</Badge>}
                    {event.outsideFilters && (
                      <Badge variant="outline">Outside filters</Badge>
                    )}
                    {event.promptMatch != null && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className={cn(
                              "cursor-default",
                              event.promptMatched
                                ? "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-300"
                                : "border-muted-foreground/20 bg-muted text-muted-foreground",
                            )}
                          >
                            Prompt {Math.round(event.promptMatch * 100)}%
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          {event.promptMatched
                            ? "Structural match: every dimension the intent specified is on this event's own fields — the claim copy may cite."
                            : "Semantic similarity to the prompt only — no full structural match on the intent's terms."}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {event.unscoredReason && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="cursor-default">
                            Unscored
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>{event.unscoredReason}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-[320px] text-sm text-muted-foreground">
                  {event.sentence && (
                    <div className="italic text-foreground">{event.sentence}</div>
                  )}
                  {event.groupExplanation ? (
                    <div className={cn(event.sentence && "mt-1 text-xs")}>
                      {event.groupExplanation}
                    </div>
                  ) : (
                    !event.sentence && "—"
                  )}
                </TableCell>
              </TableRow>
              {isExpanded && (
                <TableRow>
                  <TableCell colSpan={7} className="bg-muted/20 p-0">
                    <MemberScoreTable
                      perMember={event.perMember}
                      memberNameById={memberNameById}
                    />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

function MemberScoreTable({
  perMember,
  memberNameById,
}: {
  perMember: BlendMemberScore[];
  memberNameById: Map<string, string>;
}) {
  if (perMember.length === 0) {
    return (
      <div className="p-3 text-xs text-muted-foreground">
        No per-member scores recorded.
      </div>
    );
  }

  return (
    <div className="p-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead className="w-20 text-right">Score</TableHead>
            <TableHead className="w-28">Hop</TableHead>
            <TableHead>Facts</TableHead>
            <TableHead className="w-40">Debug</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {perMember.map((member) => (
            <TableRow key={member.userId}>
              <TableCell className="font-medium">
                {memberNameById.get(member.userId) ?? member.userId}
              </TableCell>
              <TableCell className="text-right">
                {Math.round(member.memberScore * 100)}
              </TableCell>
              <TableCell>
                {member.hop === null ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className={cn("cursor-default", hopBadgeClassName(member.hop))}
                      >
                        {formatHop(member.hop)}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      The graph never reached this member for this event — scored on
                      embedding fit alone.
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Badge variant="outline" className={hopBadgeClassName(member.hop)}>
                    {formatHop(member.hop)}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {member.facts.length > 0 ? (
                  <ul className="list-disc space-y-0.5 pl-4 text-xs">
                    {member.facts.map((fact, index) => (
                      <li key={index}>{fact}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="font-mono text-[10px] text-muted-foreground">
                cos {member.cosine.toFixed(2)} · fit {member.fit.toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} className="h-12 w-full" />
      ))}
    </div>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
      <div className="text-sm font-medium">{title}</div>
      <div className="mt-1 max-w-md text-xs text-muted-foreground">
        {description}
      </div>
    </div>
  );
}

function signalBadgeClassName(signal: BlendMember["signal"]): string {
  switch (signal) {
    case "Full":
      return "";
    case "SyntheticGenres":
      return "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";
    case "LowSignal":
      return "border-destructive/30 bg-destructive/10 text-destructive";
    default:
      return "";
  }
}

// hop 0 is the STRONGEST signal (the member engages with this exact event), not the weakest —
// the inverted reading of this exact field (as pprNorm) was the bug that took the page down.
// Colours mirror the real backend tiers (0→1.00, 1→0.80, 2→0.50, 3+→0.25); null falls back to
// embedding fit alone and is neither good nor bad, so it gets the same neutral treatment as the
// farthest hops rather than a "Poor"-style warning colour that no longer has any basis in the API.
function hopBadgeClassName(hop: BlendMemberScore["hop"]): string {
  switch (hop) {
    case 0:
      return "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-300";
    case 1:
      return "border-blue-500/20 bg-blue-500/10 text-blue-700 dark:text-blue-300";
    case 2:
      return "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300";
    default:
      return "border-muted-foreground/20 bg-muted text-muted-foreground";
  }
}

// A bare "0" reads as "nothing" to a human even though it is the strongest possible value here,
// so every tier gets a short word alongside the number.
function formatHop(hop: BlendMemberScore["hop"]): string {
  if (hop === null) return "—";
  if (hop === 0) return "0 · this event";
  return `${hop} · ${hop === 1 ? "1 step" : `${hop} steps`}`;
}

function noteBadgeClassName(note: BlendEvent["note"]): string {
  switch (note) {
    case "SafeBet":
      return "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-300";
    case "Stretch":
      return "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300";
    case "Wildcard":
      return "border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300";
    default:
      return "";
  }
}

function noteLabel(event: BlendEvent, memberNameById: Map<string, string>): string {
  if (event.note === "Stretch" && event.stretchMemberIds.length > 0) {
    const names = event.stretchMemberIds
      .map((id) => memberNameById.get(id) ?? id)
      .join(", ");
    return `Stretch for ${names}`;
  }
  return event.note;
}

function parseEmails(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\n,\s]+/)
        .map((email) => email.trim())
        .filter(Boolean),
    ),
  );
}

/** Blank or unparseable => undefined, i.e. "use the server default", never NaN. */
function parseOptionalNumber(raw: string): number | undefined {
  if (raw.trim().length === 0) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

function parseCommaList(raw: string): string[] {
  return raw
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function toUnixSeconds(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

function computeTonightRange(): { from: number; to: number } {
  const now = dayjs();
  const to = now.add(1, "day").hour(2).minute(0).second(0).millisecond(0);
  return { from: toUnixSeconds(now.toDate()), to: toUnixSeconds(to.toDate()) };
}

function computeWeekendRange(): { from: number; to: number } {
  const now = dayjs();
  const day = now.day(); // native Date.getDay() semantics: 0=Sun..6=Sat
  // Fri/Sat/Sun: the weekend is already in progress — run from now through
  // this Sunday 23:59:59; any other day: jump to the upcoming Friday 00:00.
  const isWeekendInProgress = day === 5 || day === 6 || day === 0;
  if (isWeekendInProgress) {
    const daysUntilSunday = (7 - day) % 7;
    const sunday = now.add(daysUntilSunday, "day").endOf("day");
    return {
      from: toUnixSeconds(now.toDate()),
      to: toUnixSeconds(sunday.toDate()),
    };
  }
  const daysUntilFriday = (5 - day + 7) % 7;
  const friday = now.add(daysUntilFriday, "day").startOf("day");
  const sunday = friday.add(2, "day").endOf("day");
  return {
    from: toUnixSeconds(friday.toDate()),
    to: toUnixSeconds(sunday.toDate()),
  };
}

function computeNextTwoWeeksRange(): { from: number; to: number } {
  const now = dayjs();
  return {
    from: toUnixSeconds(now.toDate()),
    to: toUnixSeconds(now.add(14, "day").toDate()),
  };
}

function buildDateRange(
  preset: DatePreset,
  customRange: CustomRange,
): { from: number; to: number } | null {
  switch (preset) {
    case "tonight":
      return computeTonightRange();
    case "weekend":
      return computeWeekendRange();
    case "next2weeks":
      return computeNextTwoWeeksRange();
    case "custom": {
      if (!customRange.from || !customRange.to) return null;
      return {
        from: toUnixSeconds(dayjs(customRange.from).startOf("day").toDate()),
        to: toUnixSeconds(dayjs(customRange.to).endOf("day").toDate()),
      };
    }
    default:
      return null;
  }
}

function extractErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (typeof data === "string" && data.trim().length > 0) {
      return data;
    }
    if (
      data &&
      typeof data === "object" &&
      typeof (data as { message?: unknown }).message === "string"
    ) {
      return (data as { message: string }).message;
    }
  }
  return toHumanErrorMessage(error);
}

export default BlendLabPage;
